import { useCallback } from 'react';
import { useStore } from 'react-redux';
import type { RootState } from '../redux/store';
import { selectMealsForDateRange, type PlannedMeal } from '../redux/mealPlanSlice';
import { selectMealSchedule } from '../redux/userPreferencesSlice';
import { isRecipeMeal } from '../redux/mealEntry';
import { addDays, weekStartOf } from '../utils/localDate';
import { ingredientLinesOf } from '../services/mealIdentity';
import { slotFitFor, type PlacedDinner, type SlotFit } from '../services/planRanking';
import { primaryMealsOf } from '../utils/slotPrimary';

/**
 * The dinners of a Monday week that weigh on its balance: accepted recipe
 * dinners, minus the one being replaced. A proposal is not on the menu yet,
 * a restaurant night or a note is not a dish, and an accompaniment is not the
 * dinner (#746): only the main of each slot counts. Exported for tests.
 */
export function placedDinnersOf(meals: PlannedMeal[], ignoreMealId?: string): PlacedDinner[] {
  return primaryMealsOf(meals).flatMap((m) => {
    if (m.mealType !== 'dinner' || m.mode === 'proposed' || m.id === ignoreMealId) return [];
    if (!isRecipeMeal(m) || !m.recipe?.title) return [];
    return [{
      date: m.scheduledFor,
      recipe: {
        title: m.recipe.title,
        ingredients: ingredientLinesOf(m.recipe),
        readyInMinutes: m.recipe.readyInMinutes,
        diets: m.recipe.diets,
        dishTypes: m.recipe.dishTypes,
      },
    }];
  });
}

/**
 * The day and week rules for a dinner suggestion list: given the date a dinner
 * slot falls on (and the dinner being swapped out, if any), what the cook's
 * schedule asks of that day and what the rest of its week already holds — the
 * `slotFit` `buildFanSuggestions` narrows with. Reads the store when called,
 * like `useSameMealExclude`, not on every render.
 */
export function useSlotFit(): (date: string, ignoreMealId?: string) => SlotFit {
  const store = useStore<RootState>();
  return useCallback((date: string, ignoreMealId?: string) => {
    const state = store.getState();
    const monday = weekStartOf(date);
    const week = selectMealsForDateRange(state, monday, addDays(monday, 6));
    return slotFitFor(date, selectMealSchedule(state), placedDinnersOf(week, ignoreMealId));
  }, [store]);
}
